// src/components/Dashboard/CoachDashboard/CoachAwardsWidget.jsx
// Shows recent awards given by this coach

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrophy, FaArrowRight, FaStar, FaMedal, FaAward } from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import styles from './CoachAwardsWidget.module.css';

const CoachAwardsWidget = ({ program, userProfile, refreshTrigger }) => {
  const navigate = useNavigate();
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch recent awards
  useEffect(() => {
    const fetchAwards = async () => {
      if (!program?._id) {
        setAwards([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const auth = getAuth();
        const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const response = await fetch(
          `${backendUrl}/api/awards/program/${program._id}`,
          { headers }
        );
        
        if (!response.ok) {
          setAwards([]);
          return;
        }

        const data = await response.json();
        const allAwards = data.awards || data || [];
        
        // Sort by date and take recent
        const recentAwards = allAwards
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 5);

        setAwards(recentAwards);
      } catch (error) {
        console.error('Error fetching awards:', error);
        setAwards([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, [program?._id, refreshTrigger]);

  const getAwardIcon = (awardType) => {
    switch (awardType?.toLowerCase()) {
      case 'individual':
        return <FaTrophy className={styles.awardIconGold} />;
      case 'team':
        return <FaMedal className={styles.awardIconBlue} />;
      case 'character':
        return <FaStar className={styles.awardIconPink} />;
      default:
        return <FaAward className={styles.awardIconGold} />;
    }
  };

  const getRecipientName = (award) => {
    if (award.recipients && award.recipients.length > 0) {
      return award.recipients[0].playerName || 'Unknown';
    }
    return 'Unknown';
  };

  const getAwardName = (award) => {
    return award.awardTemplateId?.awardName || award.awardName || 'Award';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleViewAllAwards = () => {
    if (program) {
      navigate(`/program/${program._id}/awards`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaTrophy className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Recent Awards</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaTrophy className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Recent Awards</h3>
        {awards.length > 0 && (
          <span className={styles.badge}>{awards.length}</span>
        )}
      </div>

      <div className={styles.content}>
        {awards.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>🏆</span>
            <p>No awards given yet</p>
            <span className={styles.emptyHint}>Recognize player achievements</span>
          </div>
        ) : (
          <div className={styles.awardsList}>
            {awards.map(award => (
              <div key={award._id} className={styles.awardItem}>
                <div className={styles.awardIcon}>
                  {getAwardIcon(award.awardTemplateId?.awardType)}
                </div>
                <div className={styles.awardDetails}>
                  <span className={styles.awardName}>{getAwardName(award)}</span>
                  <span className={styles.recipientName}>{getRecipientName(award)}</span>
                  <span className={styles.awardDate}>{formatDate(award.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={handleViewAllAwards}>
          {awards.length > 0 ? 'View All Awards' : 'Give Award'} <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default CoachAwardsWidget;

